'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import { memo, type ReactNode, Suspense, useState } from 'react';

import { AnalyticsProvider } from '@air/analytics';
import { LaunchDarklyProvider } from '@air/plugin-ui';
import { Spinner } from '@air/primitive-spinner';
import { TooltipProvider } from '@air/primitive-tooltip';
import { ModalProvider } from '@air/provider-modal';
import { ToastProvider } from '@air/provider-toast';
import { ErrorBoundary } from '@air/utils-error';

import { ENV } from '~/constants/Env';

export type ProvidersProps = {
  children: ReactNode;
};

const makeQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 3_000,
      },
    },
  });
};

const SuspenseFallback = memo(() => (
  <main className="flex h-full items-center justify-center bg-gray-1">
    <div className="flex flex-col items-center gap-3">
      <Spinner className="size-8 text-altitude-9" />
      <p className="text-14 text-gray-10">Loading...</p>
    </div>
  </main>
));

SuspenseFallback.displayName = 'SuspenseFallback';

export const Providers = memo(({ children }: ProvidersProps) => {
  const [queryClient] = useState(makeQueryClient);

  return (
    <ErrorBoundary>
      <AnalyticsProvider analyticsKey={ENV.SEGMENT_KEY} isDisabled={!ENV.VERCEL_ENV}>
        <ThemeProvider attribute="data-theme" defaultTheme="system" disableTransitionOnChange>
          <LaunchDarklyProvider clientSideID={ENV.LAUNCH_DARKLY_KEY}>
            <QueryClientProvider client={queryClient}>
              <Suspense fallback={<SuspenseFallback />}>
                <ToastProvider>
                  <TooltipProvider>
                    <ModalProvider>{children}</ModalProvider>
                  </TooltipProvider>
                </ToastProvider>
              </Suspense>
            </QueryClientProvider>
          </LaunchDarklyProvider>
        </ThemeProvider>
      </AnalyticsProvider>
    </ErrorBoundary>
  );
});

Providers.displayName = 'Providers';
